import { BillingCycle } from "@next-platform/client-common-core/user/types";
import { Icon, IconSize, IconVariants, useMedia } from "@next-platform/client-common-spicy-ui";
import { Popover, PopoverTrigger, Selection } from "@nextui-org/react";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Trans from "@/components/Trans/Trans";
import { IPlanData } from "@/types";
import findPurchasePlanName from "@/utils/findPurchasePlanName";

import { Col, Row, SpicyButton, Text } from "../customComponents";
import PlanPopoverContent from "./PlanPopoverContent";
import { OrderSummaryItemsProps } from "./types";

const OrderSummaryItems = (props: OrderSummaryItemsProps) => {
  const { isMobile } = useMedia();
  const { items, billingPeriod, updateCoupon, purchaseOptions } = props;
  const [stateIsPlanDropdownOpen, setStateIsPlanDropdownOpen] = useState(false);
  const [stateSelectedFrequency, setStateSelectedFrequency] = useState<BillingCycle>(billingPeriod);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedPlanId = queryParams.get("plan_id");
  const flowParam = queryParams.get("flow");
  const navigate = useNavigate();
  const plans = items.map((orderItem) => orderItem?.name).join(", ");
  const hasQuarterlyPlans = Boolean(purchaseOptions?.quarterly_plans?.length);
  const effectiveFrequency = !hasQuarterlyPlans && stateSelectedFrequency === BillingCycle.QUARTERLY ? BillingCycle.MONTHLY : stateSelectedFrequency;
  const plansByCycle: Record<BillingCycle, IPlanData[] | undefined> = {
    [BillingCycle.MONTHLY]: purchaseOptions?.monthly_plans,
    [BillingCycle.QUARTERLY]: purchaseOptions?.quarterly_plans,
    [BillingCycle.ANNUAL]: purchaseOptions?.annual_plans,
  };
  const selectedPlans = plansByCycle[effectiveFrequency] ?? [];

  const handlePlanChange = useCallback(
    (selection: Selection) => {
      const planId = [...selection][0];
      updateCoupon?.("");
      const newParams = new URLSearchParams({ plan_id: String(planId) });
      if (flowParam) {
        newParams.set("flow", flowParam);
      }
      navigate(`/checkout?${newParams.toString()}`, { replace: true });
    },
    [flowParam, navigate, updateCoupon],
  );

  const handleMenuClose = useCallback(() => {
    setStateSelectedFrequency(billingPeriod);
  }, [billingPeriod]);

  const handleFrequencyChange = useCallback((key: unknown) => {
    setStateSelectedFrequency(key as BillingCycle);
  }, []);

  return (
    <Popover className="w-[320px]" onOpenChange={setStateIsPlanDropdownOpen} onClose={handleMenuClose}>
      <PopoverTrigger>
        <SpicyButton variant="primary_smallest" color="transparent" className="items-center" disableRipple>
          <Col>
            <Row justify={isMobile ? undefined : "center"}>
              <Text variant={"md"} type={"font-[600]"}>
                {findPurchasePlanName(purchaseOptions, selectedPlanId) ?? plans}{" "}
              </Text>
            </Row>
            <Row className={"items-center flex-row-reverse " + (isMobile ? "justify-end" : "justify-center pl-6")}>
              <Icon
                icon={IconVariants.CHEVRON_UP}
                size={IconSize.SMALL}
                className={stateIsPlanDropdownOpen ? undefined : "rotate-180 relative top-[1px]"}
              />
              <Text variant={"xs"} type="underline font-light tracking-wide">
                <Trans i18nKey="subscription:checkout.changePlan" />
              </Text>
            </Row>
          </Col>
        </SpicyButton>
      </PopoverTrigger>
      <PlanPopoverContent
        effectiveFrequency={effectiveFrequency}
        hasQuarterlyPlans={hasQuarterlyPlans}
        onFrequencyChange={handleFrequencyChange}
        selectedPlanId={selectedPlanId}
        selectedPlans={selectedPlans}
        onPlanChange={handlePlanChange}
      />
    </Popover>
  );
};

export default OrderSummaryItems;
